/**
 * BillingUpgrade — dedicated page where org-admins choose & purchase a plan.
 *
 * Renders a card grid of available paid plans, lets the admin pick one,
 * and start a Stripe subscription
 * using their saved payment method. If no payment method exists, sends them
 * back to /billing to add one.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js/pure';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowLeft,
    CheckCircleFill,
    XCircleFill,
    LightningFill,
    StarFill,
    CreditCard2FrontFill,
    InfoCircle,
} from 'react-bootstrap-icons';

import {
    fetchAvailablePlans,
    fetchCurrentPlan,
    type CurrentPlanResponse,
    type SubscriptionPlanFull,
} from '../api/plans';
import {
    changeSubscriptionPlan,
    confirmLLMUsageCreditsRecharge,
    createSubscription,
    fetchInitialUsageCreditCompletion,
    fetchPublicConfig,
    fetchPaymentMethod,
    fetchSubscriptionStatus,
    syncSubscription,
    type BillingPaymentMethod,
    type SubscriptionStatus,
} from '../api/stripe_billing';
import { getGuidedTourStepIndexById } from '../tour/dashboardGuidedTourConfig';
import { markOnboardingBillingDecisionCompleted } from '../tour/guidedTourStorage';
import BillingPaymentProgressModal, {
    type BillingPaymentProgressStep,
} from '../components/BillingPaymentProgressModal';
import './BillingUpgrade-new.css';

function formatUsd(cents: number): string
{
    return `$${(cents / 100).toFixed(2)}`;
}

function formatBillingInterval(interval: string): string
{
    if (interval === 'day') return 'day';
    if (interval === 'month') return 'month';
    if (interval === 'year') return 'year';
    return interval || 'period';
}

function normalizedMonthlyCents(plan: { price_cents: number; billing_interval: string } | null): number
{
    if (!plan) return 0;
    if (plan.billing_interval === 'day') return plan.price_cents * 30;
    if (plan.billing_interval === 'year') return plan.price_cents / 12;
    return plan.price_cents;
}

function normalizedMonthlyLabel(plan: { price_cents: number; billing_interval: string } | null): string
{
    if (!plan) return '$0.00/month equivalent';
    return `${formatUsd(Math.round(normalizedMonthlyCents(plan)))}/month equivalent`;
}

const BILLING_REQUEST_STORAGE_VERSION = 1;
const INITIAL_CREDIT_COMPLETION_POLL_LIMIT = 45;
const INITIAL_CREDIT_COMPLETION_POLL_DELAY_MS = 1000;

function getBillingRequestKey(storageKey: string): string
{
    try
    {
        const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (
            stored?.version === BILLING_REQUEST_STORAGE_VERSION
            && typeof stored?.key === 'string'
            && stored.key
        )
        {
            return stored.key;
        }
    }
    catch
    {
        // Legacy keys were plain strings and must not be reused with new request shapes.
    }

    const key = crypto.randomUUID();
    sessionStorage.setItem(
        storageKey,
        JSON.stringify({ version: BILLING_REQUEST_STORAGE_VERSION, key }),
    );
    return key;
}

function shouldDiscardBillingRequestKey(error: any): boolean
{
    const status = Number(error?.response?.status || 0);
    return status >= 400 && status < 500;
}

function BillingUpgrade(): React.ReactElement
{
    const navigate = useNavigate();
    const noPlanMessage = 'No subscription plan is available for your organization. Please contact the AcceliQA Team.';
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState<SubscriptionPlanFull[]>([]);
    const [currentPlan, setCurrentPlan] = useState<CurrentPlanResponse | null>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<BillingPaymentMethod | null>(null);
    const [llmUsageRechargeAmountCents, setLlmUsageRechargeAmountCents] = useState(5000);
    const [error, setError] = useState<string | null>(null);

    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [subscribing, setSubscribing] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [paymentProgress, setPaymentProgress] = useState<BillingPaymentProgressStep[] | null>(null);
    const activeBillingRequestKey = useRef<string | null>(null);

    const updatePaymentProgress = (id: string, state: BillingPaymentProgressStep['state'], detail: string): void =>
    {
        setPaymentProgress((current) => current?.map((step) => (
            step.id === id ? { ...step, state, detail } : step
        )) || null);
    };

    const paymentInProgress = paymentProgress?.some((step) => !['succeeded', 'failed'].includes(step.state)) === true;

    useEffect(() =>
    {
        if (!paymentInProgress) return undefined;

        const preventExit = (event: BeforeUnloadEvent): void =>
        {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', preventExit);
        return () => window.removeEventListener('beforeunload', preventExit);
    }, [paymentInProgress]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [plansRes, currentRes, pmRes, statusRes] = await Promise.all([
                fetchAvailablePlans(),
                fetchCurrentPlan(),
                fetchPaymentMethod(),
                fetchSubscriptionStatus(),
            ]);
            setPlans(plansRes.paid_plans);
            setLlmUsageRechargeAmountCents(plansRes.llm_usage_recharge_amount_cents || 5000);
            setCurrentPlan(currentRes);
            setPaymentMethod(pmRes);
            setSubscriptionStatus(statusRes);
            setSelectedPlanId((prev) => {
                if (prev != null && plansRes.paid_plans.some((p) => p.id === prev)) return prev;
                return plansRes.paid_plans[0]?.id ?? null;
            });
        }
        catch (exc: any) {
            setError(exc?.response?.data?.detail || exc?.message || 'Failed to load plans');
        }
        finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void loadAll(); }, [loadAll]);
    const selectedPlan = useMemo(
        () => plans.find((p) => p.id === selectedPlanId) || null,
        [plans, selectedPlanId]
    );
    const activePlan = currentPlan?.plan || subscriptionStatus?.plan || null;
    const initialCreditsCents = llmUsageRechargeAmountCents;
    const currentUsageCredits = subscriptionStatus?.llm_usage_credits;
    const initialCreditsChargeRequired = !currentUsageCredits
        || currentUsageCredits.available_balance_after_debt_cents
            <= currentUsageCredits.auto_recharge_threshold_cents;
    const hasSavedPaymentMethod = !!paymentMethod?.has_payment_method;
    const hasActivePaidSubscription =
        subscriptionStatus?.has_active_subscription === true &&
        ['active', 'trialing'].includes(subscriptionStatus.subscription_status || '');
    const sameActivePlan = !!(hasActivePaidSubscription && activePlan && selectedPlan && activePlan.id === selectedPlan.id);
    const reactivatingCancelledPlan = sameActivePlan && subscriptionStatus?.cancel_at_period_end === true;
    const planChangeKind = !hasActivePaidSubscription
        ? 'new'
        : reactivatingCancelledPlan
            ? 'reactivate'
            : sameActivePlan
                ? 'unchanged'
                : normalizedMonthlyCents(selectedPlan) > normalizedMonthlyCents(activePlan)
                    ? 'upgrade'
                    : 'downgrade';
    const currentMonthlyEquivalent = normalizedMonthlyCents(activePlan);
    const selectedMonthlyEquivalent = normalizedMonthlyCents(selectedPlan);
    const monthlyEquivalentDifference = Math.abs(selectedMonthlyEquivalent - currentMonthlyEquivalent);
    const monthlyComparisonText = activePlan && selectedPlan
        ? `${formatUsd(Math.round(selectedMonthlyEquivalent))} - ${formatUsd(Math.round(currentMonthlyEquivalent))} = ${formatUsd(Math.round(monthlyEquivalentDifference))}/month equivalent ${planChangeKind === 'upgrade' ? 'increase' : 'decrease'}`
        : '';
    const dueTodayLabel = planChangeKind === 'new'
        ? formatUsd((selectedPlan?.price_cents || 0) + (initialCreditsChargeRequired ? initialCreditsCents : 0))
        : planChangeKind === 'upgrade'
            ? 'Calculated by Stripe'
            : '$0.00';
    const planChangeNotice = planChangeKind === 'upgrade'
        ? `You are moving to a higher-cost plan: ${normalizedMonthlyLabel(activePlan)} today to ${normalizedMonthlyLabel(selectedPlan)}. Basic comparison: ${monthlyComparisonText}. It applies immediately, and Stripe calculates today’s prorated amount after applying any unused credit from your current plan.`
        : planChangeKind === 'downgrade'
            ? `You are moving to a lower-cost plan: ${normalizedMonthlyLabel(activePlan)} today to ${normalizedMonthlyLabel(selectedPlan)}. Basic comparison: ${monthlyComparisonText}. It is scheduled for the next renewal, your current plan remains active until then, and no subscription charge is created today.`
            : planChangeKind === 'reactivate'
                ? 'This restores auto-renewal for your current plan. No subscription charge is created today.'
                : planChangeKind === 'unchanged'
                    ? 'You are already on this plan. No subscription change or charge is created.'
                    : initialCreditsChargeRequired
                        ? 'A new subscription starts immediately. The first subscription payment and initial LLM Usage Credits are charged today.'
                        : 'A new subscription starts immediately. Your existing LLM Usage Credits remain available, so no initial credit recharge is needed today.';
    const llmCreditChargeNotice = planChangeKind === 'new'
        ? initialCreditsChargeRequired
            ? `${formatUsd(initialCreditsCents)} in initial LLM Usage Credits will be charged today because your available balance is at or below the Auto-Recharge Threshold.`
            : 'Your available LLM Usage Credits are above the Auto-Recharge Threshold, so no additional credits will be charged today.'
        : 'This plan change does not charge additional LLM Usage Credits today. Existing credit balance remains as-is; future auto-recharges may still occur if usage reaches the configured threshold.';
    const actionLabel = planChangeKind === 'reactivate'
        ? 'Reactivate Plan'
        : hasActivePaidSubscription
            ? 'Confirm Change'
            : 'Subscribe Now';
    const disablePlanAction = subscribing || !hasSavedPaymentMethod || planChangeKind === 'unchanged';

    async function handleSubscribe() {
        if (activeBillingRequestKey.current) return;
        if (!selectedPlanId) {
            setError('Pick a plan to continue.');
            return;
        }
        if (!hasSavedPaymentMethod) {
            setError('Add a payment method on the Billing page before subscribing.');
            return;
        }
        setSubscribing(true);
        const requestStorageKey = `acceliqa:billing:${hasActivePaidSubscription ? 'change' : 'subscribe'}:${selectedPlanId}`;
        const requestKey = getBillingRequestKey(requestStorageKey);
        activeBillingRequestKey.current = requestKey;
        setError(null);
        setSuccessMsg(null);
        setPaymentProgress(
            hasActivePaidSubscription
                ? [{
                    id: 'subscription',
                    label: 'Subscription payment',
                    state: 'processing',
                    detail: 'Contacting Stripe to update your plan.',
                }]
                : [
                    {
                        id: 'subscription',
                        label: 'Subscription payment',
                        state: 'processing',
                        detail: 'Creating your subscription with Stripe.',
                    },
                    {
                        id: 'credits',
                        label: 'Initial LLM Usage Credits',
                        state: initialCreditsChargeRequired ? 'pending' : 'succeeded',
                        detail: initialCreditsChargeRequired
                            ? 'This charge starts after the subscription payment succeeds.'
                            : 'Existing LLM Usage Credits are above the Auto-Recharge Threshold.',
                    },
                ],
        );
        try {
            const result = hasActivePaidSubscription
                ? await changeSubscriptionPlan({ plan_id: selectedPlanId, idempotency_key: requestKey })
                : await createSubscription({
                    payment_method_id: paymentMethod?.payment_method_id,
                    plan_id: selectedPlanId,
                    idempotency_key: requestKey,
                });
            if (result.requires_action) {
                updatePaymentProgress('subscription', 'verifying', 'Complete the card verification window to continue.');
                if (!result.client_secret) {
                    throw new Error('Stripe payment confirmation is not ready. Please try again.');
                }
                const config = await fetchPublicConfig();
                const stripe = await loadStripe(config.publishable_key);
                if (!stripe) throw new Error('Stripe could not be loaded for payment verification.');
                const confirmation = result.payment_intent_status === 'requires_payment_method'
                    ? await stripe.confirmCardPayment(result.client_secret, {
                        payment_method: paymentMethod!.payment_method_id!,
                    })
                    : await stripe.confirmCardPayment(result.client_secret);
                if (confirmation.error) {
                    sessionStorage.removeItem(requestStorageKey);
                    navigate('/billing', {
                        state: { billingError: confirmation.error.message || 'Subscription payment verification was not completed.' },
                    });
                    return;
                }
                const confirmedStatus = confirmation.paymentIntent?.status;
                if (confirmedStatus && !['succeeded', 'processing'].includes(confirmedStatus)) {
                    sessionStorage.removeItem(requestStorageKey);
                    navigate('/billing', {
                        state: { billingError: 'Stripe did not complete the subscription payment. Please update your payment method and try again.' },
                    });
                    return;
                }
                updatePaymentProgress('subscription', 'processing', 'Stripe is confirming the subscription payment.');
            }

            let successMessage = result.message || 'Subscription updated.';
            if (!hasActivePaidSubscription) {
                let synced = result;
                for (let attempt = 0; attempt < 6; attempt += 1) {
                    if (['active', 'trialing'].includes(synced.status)) break;
                    if (attempt > 0) {
                        await new Promise((resolve) => window.setTimeout(resolve, 750));
                    }
                    synced = await syncSubscription();
                }
                if (!['active', 'trialing'].includes(synced.status)) {
                    if (['incomplete_expired', 'past_due', 'unpaid', 'canceled'].includes(synced.status)) {
                        sessionStorage.removeItem(requestStorageKey);
                        navigate('/billing', {
                            state: { billingError: 'The subscription payment was not completed. Update your payment method and try again.' },
                        });
                        return;
                    }
                    throw new Error(
                        'Your subscription payment is still being confirmed. Please complete verification before continuing.'
                    );
                }
                updatePaymentProgress('subscription', 'succeeded', 'Subscription payment completed.');
                updatePaymentProgress('credits', 'processing', 'Preparing the initial LLM Usage Credits charge.');
                setSuccessMsg('Subscription payment confirmed. Preparing initial LLM Usage Credits...');
                let initialCreditCompleted = false;
                for (let attempt = 0; attempt < INITIAL_CREDIT_COMPLETION_POLL_LIMIT; attempt += 1) {
                    const creditCompletion = await fetchInitialUsageCreditCompletion(synced.subscription_id);
                    if (creditCompletion.state === 'succeeded' || creditCompletion.state === 'skipped') {
                        updatePaymentProgress(
                            'credits',
                            'succeeded',
                            creditCompletion.state === 'skipped'
                                ? creditCompletion.message || 'Existing LLM Usage Credits remain available.'
                                : 'Initial LLM Usage Credits payment completed.',
                        );
                        initialCreditCompleted = true;
                        break;
                    }
                    if (creditCompletion.state === 'failed') {
                        updatePaymentProgress('credits', 'failed', creditCompletion.message || 'The initial LLM Usage Credits payment failed.');
                        sessionStorage.removeItem(requestStorageKey);
                        navigate('/billing', {
                            state: {
                                billingError: creditCompletion.message
                                    || 'The initial LLM Usage Credits payment failed. Your subscription is active, but paid AI usage remains unavailable until the credits are recharged.',
                            },
                        });
                        return;
                    }
                    if (creditCompletion.state === 'requires_action') {
                        if (!creditCompletion.client_secret || !creditCompletion.stripe_payment_intent_id) {
                            throw new Error('Stripe payment verification is not ready for the initial LLM Usage Credits charge.');
                        }
                        updatePaymentProgress('credits', 'verifying', 'Complete the card verification window to finish the credit payment.');
                        setSuccessMsg('Verify your card to complete the initial LLM Usage Credits payment...');
                        const config = await fetchPublicConfig();
                        const stripe = await loadStripe(config.publishable_key);
                        if (!stripe) throw new Error('Stripe could not be loaded for payment verification.');
                        const confirmation = await stripe.confirmCardPayment(creditCompletion.client_secret);
                        if (confirmation.error) {
                            sessionStorage.removeItem(requestStorageKey);
                            navigate('/billing', {
                                state: {
                                    billingError: confirmation.error.message
                                        || 'Initial LLM Usage Credits payment verification was not completed.',
                                },
                            });
                            return;
                        }
                        if (confirmation.paymentIntent?.status === 'succeeded') {
                            await confirmLLMUsageCreditsRecharge(creditCompletion.stripe_payment_intent_id);
                            updatePaymentProgress('credits', 'processing', 'Stripe is confirming the initial LLM Usage Credits payment.');
                        }
                    }
                    await new Promise((resolve) => window.setTimeout(resolve, INITIAL_CREDIT_COMPLETION_POLL_DELAY_MS));
                }
                if (!initialCreditCompleted) {
                    throw new Error(
                        'Your subscription is active, but the initial LLM Usage Credits payment is still processing. Please wait a moment and try again.'
                    );
                }
                successMessage = synced.message || result.message || 'Subscription created.';
            }
            else if (result.requires_action) {
                await syncSubscription();
            }
            if (hasActivePaidSubscription) {
                updatePaymentProgress('subscription', 'succeeded', 'Your plan change has been confirmed.');
            }
            sessionStorage.removeItem(requestStorageKey);
            markOnboardingBillingDecisionCompleted(getGuidedTourStepIndexById('project-name'));
            setSuccessMsg(successMessage);
            // Navigate back to billing after a short pause so user sees success
            setTimeout(() => navigate('/billing'), 1200);
        }
        catch (exc: any) {
            if (shouldDiscardBillingRequestKey(exc)) {
                sessionStorage.removeItem(requestStorageKey);
            }
            const message = exc?.response?.data?.detail || exc?.message || 'Subscription failed.';
            setError(message);
            setPaymentProgress((current) => current?.map((step) => (
                step.state === 'succeeded' ? step : { ...step, state: 'failed', detail: message }
            )) || null);
        }
        finally {
            activeBillingRequestKey.current = null;
            setSubscribing(false);
        }
    }

    return (
        <div className="bu-page">
            <BillingPaymentProgressModal
                show={paymentProgress !== null}
                steps={paymentProgress || []}
                onHide={() => setPaymentProgress(null)}
            />
            <div className="bu-header">
                <button type="button" className="bu-back-btn" onClick={() => navigate('/billing')}>
                    <ArrowLeft size={14} /> Back to Billing
                </button>
                <h1 className="bu-title">Upgrade Your Plan</h1>
                <p className="bu-subtitle">
                    Choose the plan that fits your team. You&apos;ll be charged using the payment method on file.
                </p>
            </div>

            {error && (
                <div className="bu-alert bu-alert-danger">
                    <XCircleFill size={16} /> <span>{error}</span>
                </div>
            )}
            {successMsg && (
                <div className="bu-alert bu-alert-success">
                    <CheckCircleFill size={16} /> <span>{successMsg}</span>
                </div>
            )}
            {!hasSavedPaymentMethod && !loading && (
                <div className="bu-alert bu-alert-warning">
                    <InfoCircle size={16} /> <span>
                        You don&apos;t have a saved payment method. <button type="button" className="bu-link-btn" onClick={() => navigate('/billing')}>Add one on the Billing page</button> to subscribe.
                    </span>
                </div>
            )}

            {loading ? (
                <div className="bu-loading">
                    <Spinner animation="border" size="sm" /> Loading plans…
                </div>
            ) : plans.length === 0 ? (
                <div className="bu-empty">{noPlanMessage}</div>
            ) : (
                <>
                    <div className="bu-plan-grid">
                        {plans.map((plan) => {
                            const isSelected = plan.id === selectedPlanId;
                            const isCurrent = currentPlan?.plan?.id === plan.id;
                            return (
                                <button
                                    type="button"
                                    key={plan.id}
                                    className={`bu-plan-card ${isSelected ? 'bu-plan-card-selected' : ''}`}
                                    onClick={() => setSelectedPlanId(plan.id)}
                                >
                                    {isCurrent && (
                                        <span className="bu-plan-current-badge">
                                            <CheckCircleFill size={10} /> Current
                                        </span>
                                    )}
                                    {plan.is_custom && (
                                        <span className="bu-plan-custom-badge">
                                            Custom
                                        </span>
                                    )}
                                    <div className="bu-plan-name">
                                        <StarFill size={14} /> {plan.name}
                                    </div>
                                    <div className="bu-plan-price">
                                        <span className="bu-plan-price-big">{formatUsd(plan.price_cents)}</span>
                                        <span className="bu-plan-price-period">/ {formatBillingInterval(plan.billing_interval)}</span>
                                    </div>
                                    {plan.description && (
                                        <p className="bu-plan-desc">{plan.description}</p>
                                    )}
                                    <ul className="bu-plan-features">
                                        <li><CheckCircleFill size={12} /> LLM Usage Credits for platform-key calls</li>
                                        <li><CheckCircleFill size={12} /> {plan.user_limit == null ? 'Unlimited users' : `${plan.user_limit} users max`}</li>
                                        {plan.feature_flags.filter((f) => f !== 'testcase_generation').map((f) => (
                                            <li key={f}><CheckCircleFill size={12} /> {f.replaceAll('_', ' ')}</li>
                                        ))}
                                    </ul>
                                </button>
                            );
                        })}
                    </div>

                    {selectedPlan && (
                        <div className="bu-checkout-card">
                            <h2 className="bu-checkout-title">
                                <CreditCard2FrontFill size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                                Confirm subscription
                            </h2>
                            <div className="bu-checkout-summary">
                                <div>
                                    <span className="bu-checkout-summary-label">Plan</span>
                                    <span className="bu-checkout-summary-value">{selectedPlan.name}</span>
                                </div>
                                <div>
                                    <span className="bu-checkout-summary-label">Subscription</span>
                                    <span className="bu-checkout-summary-value">
                                        {formatUsd(selectedPlan.price_cents)} / {formatBillingInterval(selectedPlan.billing_interval)}
                                    </span>
                                </div>
                                <div>
                                    <span className="bu-checkout-summary-label">LLM Usage Credits</span>
                                    <span className="bu-checkout-summary-value">
                                        {planChangeKind === 'new' && !initialCreditsChargeRequired
                                            ? 'Existing balance retained'
                                            : `${formatUsd(initialCreditsCents)} initial balance`}
                                    </span>
                                </div>
                                <div>
                                    <span className="bu-checkout-summary-label">Due today</span>
                                    <span className="bu-checkout-summary-value">{dueTodayLabel}</span>
                                </div>
                                {hasSavedPaymentMethod && (
                                    <div>
                                        <span className="bu-checkout-summary-label">Payment</span>
                                        <span className="bu-checkout-summary-value">
                                            Card ending in {paymentMethod?.last4 || '••••'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="bu-alert bu-alert-info bu-plan-change-note">
                                <InfoCircle size={16} /> <span>{planChangeNotice}</span>
                            </div>

                            <div className="bu-alert bu-alert-info bu-plan-change-note">
                                <InfoCircle size={16} /> <span>{llmCreditChargeNotice}</span>
                            </div>

                            <div className="bu-alert bu-alert-warning">
                                <InfoCircle size={16} /> <span>
                                    Platform mode uses LLM Usage Credits for all LLM calls. If your plan includes BYO, you can switch to BYO mode later from Configuration; in BYO mode, LLM Usage Credits are used only for vector-ingestion and embedding calls.
                                </span>
                            </div>

                            <div className="bu-actions">
                                <button
                                    type="button"
                                    className="bu-btn-secondary"
                                    onClick={() => navigate('/billing')}
                                    disabled={subscribing}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bu-btn-primary"
                                    onClick={() => void handleSubscribe()}
                                    disabled={disablePlanAction}
                                >
                                    {subscribing
                                        ? <><Spinner animation="border" size="sm" /> Processing…</>
                                        : <><LightningFill size={14} /> {actionLabel}</>}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default BillingUpgrade;
